import { cloneDeep, get, isNil, omit, set } from 'lodash';
import dayjs from 'dayjs';
import compose from '@kne/compose';
import { parsePhoneNumber, getCountryCallingCode } from 'libphonenumber-js';

export const salaryTrans = (data, name = 'salary') => {
  const salary = get(data, `${name}Max`) || get(data, `${name}Min`);
  return !isNil(salary) ? parseInt(salary / 1000) : null;
};

export const salaryTransform = {
  input: (data, name = 'salary') => {
    data = Object.assign({}, data);
    const output = omit(data, [name, `${name}Type`, `${name}Min`, `${name}Max`]);
    output[name] = salaryTrans(data, name);
    return output;
  },
  output: (data, name = 'salary', unit = 1000) => {
    data = Object.assign({}, data);
    const output = omit(data, [name, `${name}Type`, `${name}Min`, `${name}Max`]);
    const salary = get(data, name, null) ? get(data, name, null) * unit : null;
    output[name] = salary;
    output[`${name}Min`] = salary;
    output[`${name}Max`] = salary;
    return output;
  }
};

export const timeTransform = {
  input: data => {
    data = Object.assign({}, data);
    const output = omit(data, ['startTime', 'endTime', 'soFar']);
    if (data.startTime && (data.soFar || data.endTime)) {
      output.time = [new Date(data.startTime), data.soFar ? 'sofar' : new Date(data.endTime)];
    }
    return output;
  },
  output: data => {
    data = Object.assign({}, data);
    const output = omit(data, ['time']);
    if (data.time && Array.isArray(data.time) && data.time.length === 2) {
      output.startTime = dayjs(data.time[0]).valueOf().toISOString();
      output.time[1] === 'sofar' ? (output.soFar = true) : (output.endTime = dayjs(data.time[1]).valueOf().toISOString());
    }
    return output;
  }
};

export const parsePhoneInput = (data, name = 'phone') => {
  const value = get(data, name);
  if (isNil(value) || isNil(parsePhoneNumber)) {
    return null;
  }
  const parseValue = value ? parsePhoneNumber(value) : null;

  if (!get(parseValue, 'nationalNumber')) {
    return null;
  }
  return { code: get(parseValue, 'country'), value: get(parseValue, 'nationalNumber') };
};

export const phoneTransform = {
  input: (data, name = 'phone') => {
    data = Object.assign({}, data);
    const output = omit(data, [name, `${name}Code`]);
    if (!isNil(data.phone)) {
      output[name] = parsePhoneInput(data, name);
    }
    return output;
  },
  output: (data, name = 'phone') => {
    data = Object.assign({}, data);
    const output = omit(data, [name, `${name}Code`]);
    if (data[`${name}Code`] && data[`${name}Code`].value) {
      const phoneCode = getCountryCallingCode(data[`${name}Code`].code || 'CN');
      output[name] = '+' + phoneCode + ' ' + data[`${name}Code`].value;
    }
    return output;
  }
};

export const typeTransform = {
  input: (data, name = 'card') => {
    data = Object.assign({}, data);
    const output = omit(data, [name, `${name}Type`]);
    if (!isNil(data[name])) {
      output[name] = {
        value: data[name],
        type: data[`${name}Type`]
      };
    }
    return output;
  },
  output: (data, name = 'card') => {
    data = Object.assign({}, data);
    const output = omit(data, [name]);
    if (data[name] && data[name].value) {
      output[name] = data[name].value;
      output[`${name}Type`] = data[name].type;
    }
    return output;
  }
};

export const valueToCodeTransform = {
  input: (data, name) => {
    data = Object.assign({}, data);
    const output = omit(data, [name, `${name}Code`]);
    if (!isNil(data[`${name}Code`])) {
      output[name] = {
        value: data[`${name}Code`],
        label: data[name]
      };
    }
    return output;
  },
  output: (data, name) => {
    data = Object.assign({}, data);
    const output = omit(data, [name]);
    if (data[name] && !isNil(data[name].value)) {
      output[`${name}Code`] = data[name].value;
      output[name] = data[name].label;
    }
    return output;
  }
};

export const valueToCodeArrayTransform = {
  input: (data, name) => {
    data = Object.assign({}, data);
    const output = omit(data, [name, `${name}Code`]);
    if (Array.isArray(data[`${name}Code`])) {
      output[name] = data[`${name}Code`].map((value, index) => {
        return {
          value,
          label: get(data[name], `[${index}]`)
        };
      });
    }
    return output;
  },
  output: (data, name) => {
    data = Object.assign({}, data);
    const output = omit(data, [name, `${name}Code`]);
    if (Array.isArray(data[`${name}Code`])) {
      output[`${name}Code`] = data[`${name}Code`].map(({ value }) => value);
      output[name] = data[`${name}Code`].map(({ label }) => label);
    }
    return output;
  }
};
export const jobIntentionTransform = {
  input: (data, name) => {
    data = Object.assign({}, data);
    const output = omit(data, [name]);
    if (Array.isArray(data[name])) {
      output[name] = data[name].map(item => Object.assign({}, item, salaryTransform.input(item)));
    }
    return output;
  },
  output: (data, name) => {
    data = Object.assign({}, data);
    const output = omit(data, [name]);
    if (Array.isArray(data[name])) {
      output[name] = data[name].map(item => ({
        ...item,
        ...salaryTransform.output(item, 'salary'),
        function: get(item, 'functionCode.label') || get(data, 'expectFunction[0]'),
        functionCode: get(item, 'functionCode.value') || get(item, 'functionCode'),
        industry: get(item, 'industryCode', []).map(({ label }) => label),
        industryCode: get(item, 'industryCode', []).map(({ value }) => value)
      }));
    }
    return output;
  }
};

export const timeArrayTransform = {
  input: (data, name = 'educations') => {
    data = Object.assign({}, data);
    const output = omit(data, [name]);
    if (Array.isArray(data[name])) {
      output[name] = data[name].map(({ startTime, soFar, endTime, ...item }) => {
        return {
          ...item,
          salary: salaryTrans(item, 'salary'),
          time: startTime && (soFar || endTime) ? [dayjs(startTime), soFar ? 'sofar' : dayjs(endTime)] : null
        };
      });
    }
    return output;
  },
  output: (data, name = 'educations') => {
    data = Object.assign({}, data);
    const output = omit(data, [name]);
    if (Array.isArray(data[name])) {
      output[name] = data[name].map(({ time, ...item }) => {
        return {
          ...item,
          startTime: time?.[0] ? dayjs(time[0]).format('YYYY-MM-DD') : null,
          endTime: time?.[1] && time[1] !== 'sofar' ? dayjs(time[1]).format('YYYY-MM-DD') : null,
          soFar: time?.[1] && time[1] === 'sofar'
        };
      });
    }
    return output;
  }
};

export const resumeTransform = {
  input: compose(
    data => typeTransform.input(data, 'card'),
    // salaryTransform.input,
    // data => salaryTransform.input(data, 'currentSalary'),
    data => salaryTransform.input(data, 'expectedSalary'),
    timeArrayTransform.input,
    data => timeArrayTransform.input(data, 'works'),
    data => timeArrayTransform.input(data, 'projects'),
    data => valueToCodeArrayTransform.input(data, 'function'),
    data => valueToCodeArrayTransform.input(data, 'industry'),
    data => valueToCodeArrayTransform.input(data, 'expectFunction'),
    data => valueToCodeArrayTransform.input(data, 'expectedIndustry'),
    data => jobIntentionTransform.input(data, 'jobIntention')
  ),
  output: compose(
    phoneTransform.output,
    data => typeTransform.output(data, 'card'),
    // salaryTransform.output,
    data => salaryTransform.output(data, 'currentSalary'),
    data => salaryTransform.output(data, 'expectedSalary'),
    timeArrayTransform.output,
    data => timeArrayTransform.output(data, 'works'),
    data => timeArrayTransform.output(data, 'projects'),
    data => valueToCodeArrayTransform.output(data, 'function'),
    data => valueToCodeArrayTransform.output(data, 'industry'),
    data => valueToCodeArrayTransform.output(data, 'expectFunction'),
    data => valueToCodeArrayTransform.output(data, 'expectedIndustry'),
    data => jobIntentionTransform.output(data, 'jobIntention')
  )
};

export const inputFormData = inputData => {
  let data = cloneDeep(inputData);
  if (data?.phone) {
    data.phoneCode = {
      value: data?.phone,
      code: data?.phoneRegionCode || 'CN'
    };
  }
  if (data?.otherPhone) {
    data.otherPhoneCode = {
      value: data?.otherPhone,
      code: data?.otherPhoneRegionCode || 'CN'
    };
  }
  if (data?.card) {
    data.cardNumber = {
      value: data.card,
      type: [2]
    };
  }

  return data;
};

export const outputFormData = outputData => {
  let data = cloneDeep(outputData);
  const { otherPhoneCode, phoneCode, cardNumber } = data;

  if (phoneCode?.code) {
    const { code, value } = phoneCode;
    data.phone = value;
    data.phoneRegionCode = code;
    delete data.phoneCode;
  }
  if (otherPhoneCode?.code) {
    const { code, value } = otherPhoneCode;
    data.otherPhone = value;
    data.otherPhoneRegionCode = code;
    delete data.otherPhoneCode;
  }
  if (cardNumber?.value) {
    const { type, value } = cardNumber;
    data.cardType = type[0];
    data.card = value;
    delete data.cardNumber;
  }

  return data;
};

const transform = {
  salaryTrans,
  salaryTransform,
  timeTransform,
  phoneTransform,
  parsePhoneInput,
  typeTransform,
  valueToCodeTransform,
  valueToCodeArrayTransform,
  resumeTransform,
  inputFormData,
  outputFormData
};

export default transform;
